import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allActionsStore } from "../../stores/actions/all_actions_store"
import { allUsersStore } from "../../stores/organization/all_users_store"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"
import { WILDCARD } from "../../lib/constants/permissions_constants"

/**
 * User Permissions Controller
 *
 * Manages the user permissions selection interface within the permissions modal:
 * - Fetches and displays available managed policies for multi-select
 * - Shows selected managed policy grants as read-only badges
 * - Renders a hierarchical checkbox tree for inline actions
 * - Handles checkbox interactions with parent/child relationships
 * - Manages the wildcard selection ("All Actions")
 * - Shows effective (combined) actions from managed + inline
 * - Saves selected permissions via Api::UsersController with managed_policy_ids + inline_permissions
 *
 * Facility/locale access is configured on Projects, not in this modal.
 *
 * Connects to data-controller="forms--user-permissions"
 */
export default class extends Controller {
    static targets = [
        "actionsTreeContainer",
        "allActionsCheckbox",
        "adminRoleCheckbox",
        "saveButton",
        "modalTitle",
        "managedPoliciesLoading",
        "noPoliciesMessage",
        "managedPoliciesContainer",
        "managedPolicyGrantsContainer",
        "managedPolicyGrantsList",
        "effectivePermissionsCard",
        "effectiveActionsList"
    ]

    static stores = [
        allActionsStore,
        allUsersStore
    ]

    connect() {
        // Initialize stores
        useStore(this)

        // Initialize selection state
        this.selectedActions = new Set()
        this.isAdmin = false
        this.userId = null
        this.userEmail = null

        // Managed policy state
        this.availablePolicies = []
        this.selectedPolicyIds = new Set()
        this.currentUserManagedPolicyIds = []

        // Store available permissions data
        this.availableActions = {}

        // Listen for modal open event from permissions-form controller
        this.boundHandleModalOpen = this.handleModalOpen.bind(this)
        window.addEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)

        // Load available permissions from API
        this.loadAvailablePermissions()

        // Load available managed policies
        this.loadManagedPolicies()
    }

    disconnect() {
        // Clean up event listener
        window.removeEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)
    }

    /**
     * React to allActionsStore updates.
     * When the actions data finishes loading, re-render the effective permissions
     * and managed policy grants so that action identifiers resolve to descriptions.
     */
    onAllActionsStoreUpdate() {
        // Guard: don't process store updates until initialization is complete
        if (!this.selectedPolicyIds) {
            return
        }

        this.renderManagedPolicyGrants()
        this.updateEffectivePermissions()
    }

    /**
     * Handle modal open event
     * Receives user data from permissions-form controller
     *
     * @param {CustomEvent} event - Event with user data in detail
     */
    handleModalOpen(event) {
        const { userId, name, email, currentPolicies, admin } = event.detail

        this.userId = userId
        this.userEmail = email
        this.isAdmin = !!admin  // Convert to boolean (1000 -> true, nil -> false)

        // Update modal title with user information
        if (this.hasModalTitleTarget) {
            this.modalTitleTarget.textContent = `Configure ${ name } (${ email }) Permissions`
        }

        // Parse policies: handle both legacy hash format and new array format
        this.parsePolicies(currentPolicies)

        // Update tree controllers with current inline selections
        this.updateTreeControllers()

        // Update wildcard checkboxes and admin role
        this.updateWildcardCheckboxes()

        // Update managed policy checkboxes
        this.updateManagedPolicyCheckboxes()

        // Render managed policy grants display
        this.renderManagedPolicyGrants()

        // Update effective permissions display
        this.updateEffectivePermissions()
    }

    /**
     * Parse user policies into managed policy IDs and inline permissions.
     * Handles both legacy Hash format and new Array format.
     *
     * @param {Object|Array} policies - User's current policies
     */
    parsePolicies(policies) {
        this.selectedPolicyIds = new Set()
        this.selectedActions = new Set()
        this.currentUserManagedPolicyIds = []

        if (!policies) {
            return
        }

        if (Array.isArray(policies)) {
            // New array format
            policies.forEach(entry => {
                const type = entry.type || entry['type']
                if (type === 'managed') {
                    const policyId = entry.policy_id || entry['policy_id']
                    if (policyId) {
                        this.selectedPolicyIds.add(policyId)
                        this.currentUserManagedPolicyIds.push(policyId)
                    }
                } else if (type === 'inline') {
                    const actions = entry.actions || entry['actions'] || []
                    actions.forEach(a => this.selectedActions.add(a))
                }
            })
        } else if (typeof policies === 'object') {
            // Legacy hash format: { actions: [...] }
            const actions = policies.actions || []
            actions.forEach(a => this.selectedActions.add(a))
        }
    }

    /**
     * Load available permissions
     * - Actions: Handled by trees--actions-tree controller
     *
     * This controller no longer renders trees, it only manages selections
     */
    async loadAvailablePermissions() {
        // Tree controllers handle their own data loading
        // This method is kept for compatibility but does nothing
    }

    /**
     * Load available managed policies from the API.
     * Fetches all policies for the organization and renders checkboxes.
     */
    async loadManagedPolicies() {
        if (this.hasManagedPoliciesLoadingTarget) {
            this.managedPoliciesLoadingTarget.classList.remove('d-none')
        }
        if (this.hasNoPoliciesMessageTarget) {
            this.noPoliciesMessageTarget.classList.add('d-none')
        }

        try {
            const allPolicies = []
            let paginationToken = null

            do {
                const params = new URLSearchParams()
                if (paginationToken) {
                    params.append('pagination_token', paginationToken)
                }

                const response = await fetch(`/api/policies/list?${ params }`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                })

                if (!response.ok) {
                    throw new Error(`HTTP ${ response.status }: ${ response.statusText }`)
                }

                const data = await response.json()
                if (data.data && data.data.policies) {
                    allPolicies.push(...data.data.policies)
                }
                paginationToken = data.data?.pagination_token || null
            } while (paginationToken)

            this.availablePolicies = allPolicies
            this.renderManagedPolicyCheckboxes()
        } catch (error) {
            console.error('Error loading managed policies:', error)
            // If the user doesn't have admin access or policies feature isn't available,
            // just hide the loading and show no policies
            this.availablePolicies = []
            this.renderManagedPolicyCheckboxes()
        } finally {
            if (this.hasManagedPoliciesLoadingTarget) {
                this.managedPoliciesLoadingTarget.classList.add('d-none')
            }
        }
    }

    /**
     * Render managed policy checkboxes based on available policies.
     */
    renderManagedPolicyCheckboxes() {
        if (!this.hasManagedPoliciesContainerTarget) {
            return
        }

        if (this.availablePolicies.length === 0) {
            this.managedPoliciesContainerTarget.innerHTML = ''
            if (this.hasNoPoliciesMessageTarget) {
                this.noPoliciesMessageTarget.classList.remove('d-none')
            }
            return
        }

        if (this.hasNoPoliciesMessageTarget) {
            this.noPoliciesMessageTarget.classList.add('d-none')
        }

        const html = this.availablePolicies.map(policy => {
            const checked = this.selectedPolicyIds.has(policy.id) ? 'checked' : ''
            const description = policy.description ? `<small class="d-block text-muted">${ this.escapeHtml(policy.description) }</small>` : ''
            return `
                <div class="form-check mb-2 p-2 border rounded" style="padding-left: 2.5rem !important;">
                    <input class="form-check-input"
                           type="checkbox"
                           id="managed-policy-${ policy.id }"
                           value="${ policy.id }"
                           ${ checked }
                           data-action="change->forms--user-permissions#toggleManagedPolicy"
                           data-policy-id="${ policy.id }">
                    <label class="form-check-label" for="managed-policy-${ policy.id }">
                        <span class="fw-bold">${ this.escapeHtml(policy.name) }</span>
                        ${ description }
                    </label>
                </div>
            `
        }).join('')

        this.managedPoliciesContainerTarget.innerHTML = html
    }

    /**
     * Update managed policy checkboxes to reflect current selections.
     * Called when modal opens with existing user data.
     */
    updateManagedPolicyCheckboxes() {
        if (!this.hasManagedPoliciesContainerTarget) {
            return
        }

        const checkboxes = this.managedPoliciesContainerTarget.querySelectorAll('input[type="checkbox"]')
        checkboxes.forEach(checkbox => {
            const policyId = checkbox.dataset.policyId
            checkbox.checked = this.selectedPolicyIds.has(policyId)
        })
    }

    /**
     * Toggle a managed policy selection.
     * Updates the selected policies set and re-renders grants display.
     *
     * @param {Event} event - Change event from checkbox
     */
    toggleManagedPolicy(event) {
        const policyId = event.target.dataset.policyId
        const checked = event.target.checked

        if (checked) {
            this.selectedPolicyIds.add(policyId)
        } else {
            this.selectedPolicyIds.delete(policyId)
        }

        this.renderManagedPolicyGrants()
        this.updateEffectivePermissions()
    }

    /**
     * Render the read-only grants for all selected managed policies.
     * Shows each policy's actions for clear attribution.
     */
    renderManagedPolicyGrants() {
        if (!this.hasManagedPolicyGrantsContainerTarget || !this.hasManagedPolicyGrantsListTarget) {
            return
        }

        if (this.selectedPolicyIds.size === 0) {
            this.managedPolicyGrantsContainerTarget.classList.add('d-none')
            this.managedPolicyGrantsListTarget.innerHTML = ''
            return
        }

        this.managedPolicyGrantsContainerTarget.classList.remove('d-none')

        const selectedPolicies = this.availablePolicies.filter(p => this.selectedPolicyIds.has(p.id))

        const html = selectedPolicies.map(policy => {
            // Build action list items using descriptions from allActionsStore
            const actionItems = (policy.actions || []).map(a => {
                const description = this.getActionDescription(a)
                return `<li>${ this.escapeHtml(description) }</li>`
            }).join('')

            return `
                <div class="mb-3 p-2 border rounded bg-light">
                    <div class="fw-bold mb-1">
                        ${ this.escapeHtml(policy.name) }
                    </div>
                    <div class="mb-1">
                        <small class="text-muted">Actions:</small>
                        ${ actionItems ? `<ul class="mb-1 ps-3">${ actionItems }</ul>` : '<span class="text-muted">None</span>' }
                    </div>
                </div>
            `
        }).join('')

        this.managedPolicyGrantsListTarget.innerHTML = html
    }

    /**
     * The set of NON-PUBLIC action identifiers known to the client, taken from
     * allActionsStore (which the actions supplier populates with public actions already
     * filtered out). Used by updateEffectivePermissions to exclude public/implicit actions
     * from a user's effective grants. Returns an empty Set when the store has not loaded yet;
     * onAllActionsStoreUpdate re-renders once it does.
     *
     * @returns {Set<string>} non-public action identifiers
     */
    _nonPublicActionIdentifiers() {
        const ids = new Set()
        try {
            const storeData = this.allActionsStoreValue
            if (storeData && Array.isArray(storeData.actions)) {
                storeData.actions.forEach(a => {
                    if (a && a.identifier) ids.add(a.identifier)
                })
            }
        } catch (e) {
            // Store not available yet — treat as empty; re-render happens on store update.
        }
        return ids
    }

    /**
     * Get a human-readable description for an action identifier.
     * Looks up the action in the allActionsStore data.
     *
     * @param {string} actionIdentifier - The action identifier (e.g., 'items:create')
     * @returns {string} The description or the raw identifier if not found
     */
    getActionDescription(actionIdentifier) {
        if (actionIdentifier === '*') {
            return 'All Actions'
        }

        // Try to find the description from allActionsStore
        try {
            const storeData = this.allActionsStoreValue
            if (storeData && storeData.actions) {
                const action = storeData.actions.find(a => a.identifier === actionIdentifier)
                if (action && action.description) {
                    return action.description
                }
            }
        } catch (e) {
            // Store not available, fall back to identifier
        }

        return actionIdentifier
    }

    /**
     * Update the effective permissions display.
     * Shows the union of all managed policy action grants + inline actions.
     * Locale access is configured on Projects and is not shown here.
     */
    updateEffectivePermissions() {
        if (!this.hasEffectivePermissionsCardTarget) {
            return
        }

        // Gather managed policy actions with policy tracking
        const selectedPolicies = this.availablePolicies.filter(p => this.selectedPolicyIds.has(p.id))
        const managedActionSources = {} // action -> policy name

        selectedPolicies.forEach(policy => {
            (policy.actions || []).forEach(a => {
                if (!managedActionSources[a]) managedActionSources[a] = []
                managedActionSources[a].push(policy.name)
            })
        })

        // Gather inline actions (current selections)
        const inlineActions = new Set(this.selectedActions)

        // Add wildcard if checked
        if (this.hasAllActionsCheckboxTarget && this.allActionsCheckboxTarget.checked) {
            inlineActions.add(WILDCARD)
        }

        // Compute union, then drop public actions. Public routes are granted implicitly
        // to everyone and do not vary between users, so they are not part of a user's
        // *effective* (per-user) grants. allActionsStore holds ONLY non-public actions
        // (the API/supplier filter public ones out), so an identifier is kept iff it is the
        // '*' wildcard (a genuine grant meaning "all actions") or it is a known non-public
        // action present in the store. Public and unknown identifiers are excluded.
        const nonPublicIds = this._nonPublicActionIdentifiers()
        const effectiveActions = new Set(
            [...Object.keys(managedActionSources), ...inlineActions]
                .filter(action => action === WILDCARD || nonPublicIds.has(action))
        )

        // Only show effective permissions if there's something to show
        if (effectiveActions.size > 0) {
            this.effectivePermissionsCardTarget.classList.remove('d-none')
        } else {
            this.effectivePermissionsCardTarget.classList.add('d-none')
            return
        }

        // Render effective actions
        if (this.hasEffectiveActionsListTarget) {
            const actionHtml = Array.from(effectiveActions).map(action => {
                const isManaged = action in managedActionSources
                const isInline = inlineActions.has(action)

                let badgeClass, sourceLabel
                if (isManaged && isInline) {
                    badgeClass = 'bg-secondary'
                    sourceLabel = ' (policy & inline)'
                } else if (isManaged) {
                    badgeClass = 'bg-info'
                    const policies = managedActionSources[action]
                    sourceLabel = ` (${ policies.join(', ') })`
                } else {
                    badgeClass = 'bg-success'
                    sourceLabel = ' (inline)'
                }

                const displayName = this.getActionDescription(action)
                return `<span class="badge ${ badgeClass } me-1 mb-1">${ this.escapeHtml(displayName) }${ sourceLabel }</span>`
            }).join('')
            this.effectiveActionsListTarget.innerHTML = actionHtml
        }
    }

    /**
     * Update tree controllers with current selections
     * Communicates with the new tree controllers instead of rendering
     */
    updateTreeControllers() {
        // Update actions tree controller
        const actionsTreeElement = this.element.querySelector('[data-controller~="trees--actions-tree"]')
        if (actionsTreeElement) {
            const actionsController = this.application.getControllerForElementAndIdentifier(
                actionsTreeElement,
                'trees--actions-tree'
            )
            if (actionsController) {
                actionsController.currentSelectionsValue = Array.from(this.selectedActions)
            }
        }
    }

    /**
     * Update wildcard checkboxes based on current selections
     */
    updateWildcardCheckboxes() {
        // Update "All Actions" checkbox
        if (this.hasAllActionsCheckboxTarget) {
            this.allActionsCheckboxTarget.checked = this.selectedActions.has(WILDCARD)
        }

        // Update admin role checkbox
        if (this.hasAdminRoleCheckboxTarget) {
            this.adminRoleCheckboxTarget.checked = this.isAdmin
        }
    }

    /**
     * Get selections from tree controllers before saving
     */
    getSelectionsFromTreeControllers() {
        // Get selections from actions tree controller
        const actionsTreeElement = this.element.querySelector('[data-controller~="trees--actions-tree"]')
        if (actionsTreeElement) {
            const actionsController = this.application.getControllerForElementAndIdentifier(
                actionsTreeElement,
                'trees--actions-tree'
            )
            if (actionsController) {
                this.selectedActions = new Set(actionsController.getSelectedActions())
            }
        }

        // Add wildcard selection if checked
        if (this.hasAllActionsCheckboxTarget && this.allActionsCheckboxTarget.checked) {
            this.selectedActions.add(WILDCARD)
        } else if (this.hasAllActionsCheckboxTarget) {
            // If checkbox is unchecked, remove wildcard
            this.selectedActions.delete(WILDCARD)
        }
    }

    /**
     * Toggle individual action checkbox
     * No longer needed - tree controller handles this
     *
     * @param {Event} event - Change event from checkbox
     */
    toggleAction(event) {
        // Tree controller handles this
    }

    /**
     * Toggle resource wildcard checkbox
     * No longer needed - tree controller handles this
     *
     * @param {Event} event - Change event from checkbox
     */
    toggleResourceWildcard(event) {
        // Tree controller handles this
    }

    /**
     * Toggle "All Actions" wildcard
     *
     * @param {Event} event - Change event from checkbox
     */
    toggleAllActions(event) {
        const checked = event.target.checked

        if (checked) {
            // Add wildcard to selections
            this.selectedActions.add(WILDCARD)

            // Clear tree controller selections (specific actions are mutually exclusive with wildcard)
            const actionsTreeElement = this.element.querySelector('[data-controller~="trees--actions-tree"]')
            if (actionsTreeElement) {
                const actionsController = this.application.getControllerForElementAndIdentifier(
                    actionsTreeElement,
                    'trees--actions-tree'
                )
                if (actionsController) {
                    actionsController.selectedActions.clear()
                    actionsController.renderTreeFromStore()
                }
            }
        } else {
            // Remove wildcard when unchecked
            this.selectedActions.delete(WILDCARD)
        }

        this.updateEffectivePermissions()
    }

    /**
     * Called when the actions tree selection changes (specific action selected/deselected).
     * Automatically unchecks 'All Actions' when any specific action is selected.
     */
    onActionsTreeSelectionChanged() {
        const actionsTreeElement = this.element.querySelector('[data-controller~="trees--actions-tree"]')
        if (actionsTreeElement) {
            const actionsController = this.application.getControllerForElementAndIdentifier(
                actionsTreeElement,
                'trees--actions-tree'
            )
            if (actionsController && actionsController.selectedActions.size > 0) {
                // Specific actions selected — uncheck 'All Actions' and remove wildcard
                if (this.hasAllActionsCheckboxTarget) {
                    this.allActionsCheckboxTarget.checked = false
                }
                this.selectedActions.delete(WILDCARD)
            }
        }
        this.updateEffectivePermissions()
    }

    /**
     * Toggle admin role checkbox
     *
     * @param {Event} event - Change event from checkbox
     */
    toggleAdminRole(event) {
        this.isAdmin = event.target.checked
    }

    /**
     * Save selected permissions via API
     * Sends managed_policies array and policies hash to the endpoint.
     */
    async save() {
        // Get current inline selections from tree controllers
        this.getSelectionsFromTreeControllers()

        if (!this.userId) {
            sendFailureNotification('No user selected')
            return
        }

        // Disable save button and show loading state
        if (this.hasSaveButtonTarget) {
            this.saveButtonTarget.disabled = true
            this.saveButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Saving...'
        }

        try {
            const response = await fetch('/api/user/update', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    id: this.userId,
                    admin: this.isAdmin,
                    managed_policies: Array.from(this.selectedPolicyIds),
                    policies: {
                        actions: Array.from(this.selectedActions)
                    }
                })
            })

            const responseBody = await response.json()

            if (!response.ok) {
                throw new Error(responseBody.message)
            }

            if (responseBody.status === 'success') {
                // Update the allUsersStore with the updated user data
                this.updateUserInStore(responseBody.data)

                // Show success notification
                sendSuccessNotification(`${ responseBody.data.name }'s permissions updated successfully`)

                // Close modal using the permissions form controller's close method
                const permissionsFormController = this.application.getControllerForElementAndIdentifier(
                    document.getElementById('permissionsModal'),
                    'forms--permissions-form'
                )

                if (permissionsFormController) {
                    permissionsFormController.close()
                } else {
                    // Fallback: Direct DOM manipulation
                    const modalElement = document.getElementById('permissionsModal')
                    if (modalElement) {
                        modalElement.classList.remove('show')
                        modalElement.style.display = 'none'
                        modalElement.setAttribute('aria-hidden', 'true')
                        modalElement.removeAttribute('aria-modal')

                        const backdrop = document.querySelector('.modal-backdrop')
                        if (backdrop) {
                            backdrop.remove()
                        }

                        document.body.classList.remove('modal-open')
                        document.body.style.overflow = ''
                        document.body.style.paddingRight = ''
                    }
                }
            } else {
                throw new Error(responseBody.message || 'Failed to save permissions')
            }

        } catch (error) {
            console.error('Error saving permissions:', error)
            sendFailureNotification(error.message)
        } finally {
            // Re-enable save button
            if (this.hasSaveButtonTarget) {
                this.saveButtonTarget.disabled = false
                this.saveButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i>Save Permissions'
            }
        }
    }

    // ========================================
    // Helper Methods
    // ========================================

    /**
     * Update a user in the allUsersStore after saving permissions.
     * This ensures the users table and policy user counts reflect
     * the latest assigned policies without a page reload.
     *
     * @param {Object} updatedUser - The updated user data from the API response
     */
    updateUserInStore(updatedUser) {
        const currentUsers = this.allUsersStoreValue || []
        if (!Array.isArray(currentUsers) || currentUsers.length === 0) {
            return
        }

        const updatedUsers = currentUsers.map(user => {
            if (user.id === updatedUser.id) {
                return updatedUser
            }
            return user
        })

        this.setAllUsersStoreValue(updatedUsers)
    }

    /**
     * Escape HTML entities to prevent XSS in dynamically rendered content.
     *
     * @param {String} text - Text to escape
     * @returns {String} Escaped text
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }
}
